import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/i18n";

export default function Register() {
    const { lang } = useT();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const inviteToken = params.get("invite") || "";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await api.post("/register", { email: email.trim(), password, name: name.trim(), invite_token: inviteToken });
            navigate(`/register/success?email=${encodeURIComponent(email.trim())}`);
        } catch (ex) {
            setErr(ex.response?.data?.detail || "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="register-page" className="px-4 md:px-8 py-14 md:py-20 min-h-[70vh]">
            <div className="max-w-md mx-auto">
                <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#5D3FD3] mb-3">
                    {lang === "es" ? "registro" : "register"}
                </div>
                <h1 className="font-heading font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9] mb-8">
                    {lang === "es" ? "unirse." : "join."}
                </h1>
                <form onSubmit={submit} className="space-y-4">
                    <label className="block">
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#555]">
                            {lang === "es" ? "nombre" : "name"}
                        </span>
                        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-sm p-3 focus:border-[#5D3FD3] outline-none" />
                    </label>
                    <label className="block">
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#555]">
                            {lang === "es" ? "correo" : "email"}
                        </span>
                        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-sm p-3 focus:border-[#5D3FD3] outline-none" />
                    </label>
                    <label className="block">
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#555]">
                            {lang === "es" ? "contraseña (mín 6)" : "password (min 6)"}
                        </span>
                        <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-sm p-3 focus:border-[#5D3FD3] outline-none" />
                    </label>
                    {err && <div data-testid="register-error" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B91C1C]">{err}</div>}
                    <button type="submit" disabled={loading} data-testid="register-submit" className="w-full bg-[#5D3FD3] text-white hover:bg-[#111] transition-colors px-6 py-3 font-mono text-xs uppercase tracking-[0.3em]">
                        {loading ? "…" : (lang === "es" ? "crear cuenta" : "create account")}
                    </button>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#777] text-center mt-4">
                        {lang === "es" ? "¿ya tienes cuenta?" : "already have an account?"}{" "}
                        <Link to="/member/login" className="text-[#5D3FD3] underline">{lang === "es" ? "inicia sesión" : "log in"}</Link>
                    </div>
                </form>
            </div>
        </div>
    );
}

export function RegisterSuccess() {
    const { lang } = useT();
    const [params] = useSearchParams();
    const email = params.get("email") || "";
    return (
        <div data-testid="register-success" className="px-4 md:px-8 py-20 min-h-[70vh]">
            <div className="max-w-xl mx-auto">
                <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#5D3FD3] mb-3">
                    {lang === "es" ? "cuenta creada" : "account created"}
                </div>
                <h1 className="font-heading font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9] mb-6">
                    {lang === "es" ? "bienvenido." : "welcome."}<span className="text-[#5D3FD3]">.</span>
                </h1>
                <p className="font-body text-base md:text-lg text-[#333] leading-relaxed mb-8">
                    {lang === "es"
                        ? <>Tu cuenta <b>{email}</b> quedó lista. Ya puedes iniciar sesión y explorar el portal de miembros.</>
                        : <>Your account <b>{email}</b> is ready. You can log in and explore the member portal now.</>}
                </p>
                <div className="flex gap-3 flex-wrap">
                    <Link to="/member/login" data-testid="register-goto-login" className="inline-flex items-center bg-[#5D3FD3] text-white hover:bg-[#111] transition-colors px-6 py-3 font-mono text-xs uppercase tracking-[0.3em]">
                        {lang === "es" ? "iniciar sesión" : "log in"}
                    </Link>
                    <Link to="/" className="inline-flex items-center border border-[#5D3FD3] text-[#5D3FD3] hover:bg-[#5D3FD3] hover:text-white transition-colors px-6 py-3 font-mono text-xs uppercase tracking-[0.3em]">
                        {lang === "es" ? "inicio" : "home"}
                    </Link>
                </div>
            </div>
        </div>
    );
}
